import { useEffect, useState } from 'react'
import { User } from 'lucide-react'
import { useAppStore } from '@/store'
import {
  getUserAvatar,
  getUserAvatarInitial,
  USER_AVATAR_GRADIENT_CLASS,
} from '@/utils/userUtils'
import { useOrganizationDirectory } from '@/hooks/useOrganizationDirectory'

interface CommentUserAvatarProps {
  userId: string
  size?: number
}

/**
 * Avatar component for comment users that accepts a userId string
 * and properly displays avatar or initials with configurable size
 */
const CommentUserAvatar = ({ userId, size = 32 }: CommentUserAvatarProps) => {
  const { user } = useAppStore()
  const directory = useOrganizationDirectory()

  // A letter only when the name is known — this pin is where FS-581's "random
  // character such as `6`" was reported, and it came from charAt(0) of a UUID.
  const initial = getUserAvatarInitial(userId, user, directory)

  // Resolves the TODO this component used to carry: the photo IS available now
  // that the directory is loaded — the current user's own (upload, else Gmail,
  // merged by authSessionSlice) and every other member's Gmail claim.
  const photoSource = getUserAvatar(userId, user, directory)

  // A photo URL that stops resolving MUST fall back, or the browser paints its
  // own broken-image placeholder over the gradient — a question mark in Safari.
  // This is the common case, not an edge one: removing the photo on a Google
  // account kills the content-addressed URL while the claim keeps pointing at
  // it. Reset when the source or the user changes so an auth refresh or an
  // upload retries instead of latching. Same guard as UserMenu, CommentBubble
  // and ProfilePicture.
  const [photoFailed, setPhotoFailed] = useState(false)
  useEffect(() => {
    setPhotoFailed(false)
  }, [photoSource, user])
  const photo = photoFailed ? null : photoSource

  // Initials on the app-wide avatar gradient (FS-581) — matches the
  // profile bubble in the left rail instead of a per-user hash color.
  return (
    <div
      className={USER_AVATAR_GRADIENT_CLASS}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${size * 0.4}px`,
        fontWeight: 600,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {photo ? (
        <img
          src={photo}
          alt=""
          referrerPolicy="no-referrer"
          draggable={false}
          onError={() => setPhotoFailed(true)}
          style={{ width: '100%', height: '100%', borderRadius: '50%' }}
          className="object-cover"
        />
      ) : (
        initial ?? <User size={Math.round(size * 0.5)} aria-hidden />
      )}
    </div>
  )
}

export default CommentUserAvatar
